import React, { useContext, useState } from "react"
import { Div, Col, Row, Text } from "atomize"
import PrimaryButton from "../buttons/PrimaryButton"
import { theme } from "../settings/config"
import Container from "../atoms/Container"
import { motion } from "framer-motion"
import Paragraph from "../atoms/Paragraph"
import { profileContext } from "../providers/profile.provider"
import { toast } from "react-toastify"
import TextInput from "../inputs/TextInput"
import { NEW_PASSWORD_MIN_LENGTH, newPasswordIsValid } from "../utils/func.utils"
import SecondaryButton from "../buttons/SecondaryButton"

// Si entra con l'email. Il nome utente serve solo la prima volta, per dire a
// quale maestro già presente collegare il nuovo accesso: da lì in poi non
// viene più chiesto.
// Si usa in due posti: il foglio che si apre dal sito, e la pagina d'accesso
// dell'app (pages/Access.js), dove sta in una scheda bianca e i campi
// prendono il fondo caldo.
export function AuthModal({ initialMode, inCard = false }) {
	const { login, signUp, firstAccess, resetPassword } = useContext(profileContext)

	// "login" | "signup" | "firstAccess" | "resetSent"
	// dalla vetrina si arriva già sulla registrazione, non sull'accesso
	const [mode, setMode] = useState(initialMode ?? "login")
	const [email, setEmail] = useState("")
	const [password, setPassword] = useState("")
	const [repeatedPassword, setRepeatedPassword] = useState("")
	const [userName, setUserName] = useState("")
	const [activationCode, setActivationCode] = useState("")
	const [isLoading, setIsLoading] = useState(false)

	const emailIsValid = email.includes("@")
	const fieldBg = inCard ? "background" : "surface"

	const showError = error =>
		toast.error(error.cause?.text || error.message || "Qualcosa è andato storto", {
			position: "top-center",
		})

	const handleLogin = async () => {
		setIsLoading(true)
		try {
			await login(email, password)
		} catch (error) {
			showError(error)
		} finally {
			setIsLoading(false)
		}
	}

	const handleSignUp = async () => {
		if (password !== repeatedPassword) {
			toast.error("Le password non coincidono!", { position: "top-center" })
			return
		}

		setIsLoading(true)
		try {
			await signUp(email, password)
		} catch (error) {
			showError(error)
		} finally {
			setIsLoading(false)
		}
	}

	const handleFirstAccess = async () => {
		if (password !== repeatedPassword) {
			toast.error("Le password non coincidono!", { position: "top-center" })
			return
		}

		setIsLoading(true)
		try {
			await firstAccess({ username: userName, email, password, activationCode })
		} catch (error) {
			showError(error)
		} finally {
			setIsLoading(false)
		}
	}

	const handleForgotPassword = async () => {
		if (!emailIsValid) {
			toast.error("Inserisci prima la tua email", { position: "top-center" })
			return
		}
		setIsLoading(true)
		try {
			await resetPassword(email)
			setMode("resetSent")
		} catch (err) {
			toast.error(err.message || "Errore durante il recupero password", {
				position: "top-center",
			})
		} finally {
			setIsLoading(false)
		}
	}

	const isDisabled = () => {
		if (isLoading) return true
		if (mode === "login") return !emailIsValid || password === ""
		if (mode === "signup")
			return (
				!emailIsValid ||
				!newPasswordIsValid(password) ||
				!newPasswordIsValid(repeatedPassword)
			)
		return (
			userName.trim() === "" ||
			!emailIsValid ||
			!newPasswordIsValid(password) ||
			!newPasswordIsValid(repeatedPassword)
		)
	}

	const title = () => {
		if (mode === "resetSent") return "Controlla la tua email 📬"
		if (mode === "signup") return "Iniziamo 🎾"
		if (mode === "firstAccess") return "Attiva il tuo account 🔐"
		return "Heyla! Chi sei? 😃"
	}

	const Title = () => (
		<Div
			d="flex"
			justify="space-between"
			m={{ b: "0.5rem" }}
			textWeight="bold"
			textSize="title"
			textColor={theme === "light" ? "text" : "text_alt"}>
			{title()}
		</Div>
	)

	// Schermata di conferma reset
	if (mode === "resetSent") {
		return (
			<Container>
				<Title />
				<Container>
					<Div bg={theme.colors.background} rounded="20px" p="1rem" m={{ b: "1rem" }}>
						Ti abbiamo inviato un'email con il link per reimpostare la password.
						Controlla la tua casella di posta.
					</Div>
				</Container>
				<Div m={{ x: "1rem" }}>
					<Row m={{ t: "1rem" }}>
						<Col p="0">
							<PrimaryButton
								m="auto"
								bg={theme.colors.gunmetal_fill}
								textColor="white"
								textSize="subheader"
								text="Torna all'accesso"
								action={() => {
									setMode("login")
									setPassword("")
								}}
							/>
						</Col>
					</Row>
				</Div>
			</Container>
		)
	}

	const isFirstAccess = mode === "firstAccess"
	const isSignup = mode === "signup"
	// email, password e conferma: cambia solo cosa ci si fa dopo
	const needsRepeat = isFirstAccess || isSignup

	return (
		<Container>
			<Title />
			<Container>
				<motion.div
					initial={{ opacity: 0, y: 5 }}
					animate={{ opacity: 1, y: 0 }}
					exit={{ opacity: 0 }}>
					{isSignup && (
						<>
							<Text
								textSize="caption"
								textColor={theme.colors.text_light}
								m={{ b: "0.75rem" }}>
								Scegli come entrare. Subito dopo ti chiediamo il tuo nome e dove
								insegni: due minuti e sei operativo.
							</Text>

							{/* L'accettazione va raccolta qui, dove si decide: dopo la
							    registrazione sarebbe una comunicazione, non una scelta. */}
							<Text
								textSize="caption"
								textColor={theme.colors.text_light}
								m={{ b: "1rem" }}>
								Registrandoti accetti i{" "}
								<a
									href="/termini.html"
									target="_blank"
									rel="noopener noreferrer"
									style={{ color: theme.colors.primary }}>
									termini di servizio
								</a>{" "}
								e l'{" "}
								<a
									href="/privacy.html"
									target="_blank"
									rel="noopener noreferrer"
									style={{ color: theme.colors.primary }}>
									informativa sulla privacy
								</a>
								.
							</Text>
						</>
					)}

					{isFirstAccess && (
						<>
							<Text
								textSize="caption"
								textColor={theme.colors.text_light}
								m={{ b: "1rem" }}>
								Serve solo a chi il circolo ha già registrato e non è mai
								entrato. Se stai arrivando adesso torna indietro e scegli
								Registrati: non ti serve nessun codice.
							</Text>

							<Paragraph title="Nome utente">
								<TextInput
									bg={fieldBg}
									value={userName}
									handleChange={e => setUserName(e.target.value)}
									placeholder="il nome utente che ti hanno dato"
								/>
							</Paragraph>
							<Paragraph title="Codice di attivazione">
								<TextInput
									bg={fieldBg}
									value={activationCode}
									handleChange={e => setActivationCode(e.target.value)}
									placeholder="te lo dà chi gestisce il circolo"
								/>
							</Paragraph>
						</>
					)}

					<Paragraph title="Email">
						<TextInput
							bg={fieldBg}
							type="email"
							value={email}
							handleChange={e => setEmail(e.target.value)}
							placeholder="es. mario@esempio.it"
							submitAction={needsRepeat ? undefined : handleLogin}
						/>
					</Paragraph>

					<Paragraph title="Password">
						<TextInput
							bg={fieldBg}
							type="password"
							value={password}
							handleChange={e => setPassword(e.target.value)}
							submitAction={needsRepeat ? undefined : handleLogin}
						/>
						{/* senza, il bottone resta spento e non si capisce perché */}
						{needsRepeat && password !== "" && !newPasswordIsValid(password) && (
							<Text textSize="caption" textColor={theme.colors.text_light}>
								Almeno {NEW_PASSWORD_MIN_LENGTH} caratteri
							</Text>
						)}
					</Paragraph>

					{needsRepeat ? (
						<Paragraph title="Ripeti la password">
							<TextInput
								bg={fieldBg}
								type="password"
								value={repeatedPassword}
								handleChange={e => setRepeatedPassword(e.target.value)}
								submitAction={isSignup ? handleSignUp : handleFirstAccess}
							/>
						</Paragraph>
					) : (
						<Div
							textAlign="right"
							m={{ b: "0.5rem" }}
							cursor="pointer"
							onClick={handleForgotPassword}>
							<Div
								tag="span"
								textSize="caption"
								textColor={
									isLoading ? theme.colors.text_light : theme.colors.primary
								}>
								{isLoading ? "Invio in corso..." : "Password dimenticata?"}
							</Div>
						</Div>
					)}
				</motion.div>
			</Container>

			<Div m={{ x: "1rem" }}>
				<Row m={{ t: "1rem" }}>
					{needsRepeat && !isLoading && (
						<Col p="0">
							<SecondaryButton
								m="auto"
								textColor={theme.colors.gunmetal}
								textSize="subheader"
								text="Indietro"
								action={() => {
									setMode("login")
									setPassword("")
									setRepeatedPassword("")
									setActivationCode("")
								}}
							/>
						</Col>
					)}
					<Col p="0">
						<PrimaryButton
							m="auto"
							bg={theme.colors.gunmetal_fill}
							textColor="white"
							textSize="subheader"
							text={
								isLoading
									? "Caricamento..."
									: isSignup
										? "Crea l'accesso"
										: isFirstAccess
											? "Attiva e accedi"
											: "Accedi"
							}
							disabled={isDisabled()}
							action={
								isSignup
									? handleSignUp
									: isFirstAccess
										? handleFirstAccess
										: handleLogin
							}
						/>
					</Col>
				</Row>

				{!needsRepeat && (
					<>
						<Div
							textAlign="center"
							m={{ t: "1rem" }}
							cursor="pointer"
							onClick={() => setMode("signup")}>
							<Div tag="span" textSize="caption" textColor={theme.colors.text_light}>
								Non hai un account?{" "}
							</Div>
							<Div tag="span" textSize="caption" textColor={theme.colors.primary}>
								Registrati
							</Div>
						</Div>

						{/* strada di servizio: i maestri già censiti che non hanno
						    ancora un accesso lo attivano col loro codice */}
						<Div
							textAlign="center"
							m={{ t: "0.5rem" }}
							cursor="pointer"
							onClick={() => setMode("firstAccess")}>
							<Div tag="span" textSize="caption" textColor={theme.colors.text_light}>
								Il circolo ti ha già registrato? Attiva l'account
							</Div>
						</Div>
					</>
				)}
			</Div>
		</Container>
	)
}

export default AuthModal
